import React, { useState } from "react"
import _ from "lodash"
import {
	Grid,
	List,
	ListItem,
	ListItemText,
	Typography,
	Fab,
	Popover,
	Tooltip,
	Divider as MuiDivider
} from "@material-ui/core"

import {
	ExpandMore as MessageOptionsIcon,
	Smartphone as ExternalPlatformIcon,
	ListAlt as FlowResponseIcon
} from "@material-ui/icons"

import {
	Divider,
	MessageContentBox,
	InfoDialog,
	PopConfirm,
	JsonEditor
} from "@/components"
import Reactions from "@/components/Reactions"
import ChatGroupPermissionGate from "@/components/PermissionGate/ChatGroupPermissionGate"
import { MediaMessageContent } from "@/components/Messages/MediaMessage"
import CatalogueMessage from "@/components/Messages/CatalogueMessage"
import ExtendedMessage from "@/components/Messages/ExtendedMessage"

import { isSmallScreen } from "@/utils/checkDevice"
import { getInboxMessageColor } from "@/utils/message"
import { transformReactionArrayIntoString } from "@/utils/reaction"

import ReplyMessage from "@/pages/Attendance/Chat/ConversationPanel/MessageList/ReplyMessage"
import MessageFeature from "@/pages/Attendance/Chat/ConversationPanel/MessageList/MessageFeature"
import MessageDeletedContent from "@/pages/Attendance/Chat/ConversationPanel/MessageList/MessageDeletedContent"
import InboxMessageFooter from "@/pages/Attendance/Chat/ConversationPanel/MessageList/InboxMessageFooter"

import { useChatGlobalStateStore } from "@/store/ChatGlobalState"
import { useGlobalStateStore } from "@/store/GlobalState"
import useSocket from "@/hooks/useSocket"
import useCustomMemo from "@/hooks/useCustomMemo"

import HardCoded from "@/services/HardCoded"

import useStyles from "@/pages/Attendance/Chat/ConversationPanel/MessageList/CommonMessage/BaseMessage/styles"
import { Message } from "@/protocols/chatGlobalStateProtocol"
import { MessageType } from "@/protocols/channel"
import ForwardMessageDialog from "@/pages/Attendance/Chat/ConversationPanel/MessageList/CommonMessage/BaseMessage/ForwardMessageDialog"
import ColorUtil from "@/utils/ColorUtil"

export type BaseMessageProps = {
	message: Message
	childContent: React.ReactElement
	childContentFooter?: React.ReactElement
	childContainerFooter?: React.ReactElement
	messageContentBoxStyle?: React.CSSProperties
	childContentHeader?: React.ReactElement
}

const BaseMessage: React.FC<BaseMessageProps> = (props) => {
	const {
		message,
		childContent,
		childContentFooter,
		childContainerFooter,
		messageContentBoxStyle,
		childContentHeader
	} = props

	const classes = useStyles()
	const chatGlobalStateStore = useChatGlobalStateStore()
	const socket = useSocket()

	const inboxChannelChat = chatGlobalStateStore.chat.getById(message.inboxChannelChatId)
	const currentChatAttendanceStatus = chatGlobalStateStore.chat.current?.attendance?.status
	const globalStateStore = useGlobalStateStore()
	const isAdmin = globalStateStore.user.extra_data.is_admin_mode

	const messageId = message.id
	const inboxChannelChatId = message.inboxChannelChatId
	const content = message.content
	const createdAt = message.createdAt
	const deletedAt = message.deletedAt
	const extraData = message.extraData
	const feature = message.feature
	const statusMessage = message.extraData?.statusMessage
	const status = message.status
	const catalogueOrderMessage = message.extraData?.catalogueOrderMessage
	const sentByCustomer = message.sentByCustomer
	const senderName = message.senderName
	const sentByExternalPlatform = message.sentByExternalPlatform
	const type = message.type
	const uploadingMedia = message.uploadingMedia
	const messageError = message.inboxChannelChatMessageLog?.error
	const replyMessage = message.replyMessage && {
		id: message.replyMessage.id,
		caption: message.replyMessage.caption,
		content: message.replyMessage.content,
		senderName: chatGlobalStateStore.client.current?.nickname || "",
		sentByCustomer: message.replyMessage.sentByCustomer,
		type: message.replyMessage.type,
		extraData: message.replyMessage.extraData
	}
	const editedMessage = message.extraData?.editedMessage
	const reactions = message?.extraData?.reactions
	const uploadProgressInPercentage = message?.uploadProgressInPercentage

	const canViewThumbnail = HardCoded.checkFeatureFlag("canViewThumbnail") && Boolean(message.extraData?.extendedMessage?.thumbnailUrl)

	const extendedMessage = message.extraData?.extendedMessage
	const extendedMessageHasExtendedUrl = extendedMessage && (Boolean(message.extraData?.extendedMessage?.extendedUrl) || canViewThumbnail)

	const reactionsString = reactions && transformReactionArrayIntoString(reactions)
	const isWhatsappChannel = chatGlobalStateStore.chat.current?.channelType === "whatsapp"
	const isCustomerMessageWABA = chatGlobalStateStore.chat.current?.channelType === "waba" && message.sentByCustomer
	const isAbleReplyMessageOption = isWhatsappChannel || !isCustomerMessageWABA

	const isCustomerMessageInActiveChat = message.sentByCustomer && currentChatAttendanceStatus === "active"
	const canShowDeleteOption = isCustomerMessageInActiveChat && isWhatsappChannel

	const showMessageOptionsButton = (
		currentChatAttendanceStatus === "active" &&
		!message.deletedAt
	) || isAdmin

	const [messageOptionsRef, setMessageOptionsRef] = useState<HTMLElement | null>(null)
	const [isForwardDialogOpen, setIsForwardDialogOpen] = useState(false)

	const forwardableMessageTypes: MessageType[] = ["text", "picture", "video", "audio", "file"]
	const isForwardableMessageType = forwardableMessageTypes.includes(type)
	const canShowForwardOption = HardCoded.checkFeatureFlag("canForwardMessages") && isForwardableMessageType && !deletedAt

	const [optionsIconVisible, setOptionsIconVisible] = useState(false)

	const handleShowOptionsIcon = () => {
		setOptionsIconVisible(true)
	}

	const handleHideOptionsIcon = () => {
		setOptionsIconVisible(false)
	}

	const handleMessageContentBlur = (event: React.FocusEvent<HTMLElement>) => {
		const nextFocusedElement = event.relatedTarget as Node | null
		const isFocusStillInsideMessage = Boolean(nextFocusedElement) && event.currentTarget.contains(nextFocusedElement)

		if (!isFocusStillInsideMessage) {
			handleHideOptionsIcon()
		}
	}

	const handleCloseMessageOptionsPopover = () => {
		setMessageOptionsRef(null)
	}

	const handleOpenMessageOptionsPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
		setMessageOptionsRef(event.currentTarget)
	}

	const handleClose = () => {
		handleCloseMessageOptionsPopover()
		handleHideOptionsIcon()
	}

	const handleOpenForwardDialog = () => {
		handleClose()

		setIsForwardDialogOpen(true)
	}

	const handleCloseForwardDialog = () => {
		setIsForwardDialogOpen(false)
	}

	const handleReplyMessage = () => {
		handleClose()

		chatGlobalStateStore.conversationPanel.replyMessage.change(messageId)

		chatGlobalStateStore.conversationPanel.textInput.focus()
	}

	const handleDeleteMessage = () => {
		handleClose()

		if (!chatGlobalStateStore.chat.current) {
			return
		}

		const formattedMessageDate = new Date(message.createdAt)

		const beforeYesterdayDate = new Date()
		beforeYesterdayDate.setHours(beforeYesterdayDate.getHours() - 3)

		if (formattedMessageDate < beforeYesterdayDate) {
			InfoDialog.open({
				title: "Está mensagem não pode mais ser apagada",
				children: <Typography key="alert">
					Permitimos deletar mensagens até 3 horas após o envio, se for necessário deletar após esse prazo, considere fazer a exclusão pelo próprio celular!
				</Typography>,
				openDialog: true
			})
		} else {
			PopConfirm.open({
				title: "Excluir mensagem",
				description: "Deseja excluir esta mensagem?",
				confirmButtonText: "EXCLUIR",
				onConfirm: async () => {
					if (!chatGlobalStateStore.chat.current) {
						return
					}

					const { id, inboxChannelId, channelType } = chatGlobalStateStore.chat.current

					socket.deleteMessage({
						inboxChannelId,
						inboxChannelChatId: id,
						inboxChannelChatMessageId: messageId,
						channelType
					})
				}
			})
		}
	}

	const handleGetAgentDebugInfo = () => {
		return (
			<>
				{extraData?.aiAgent?.rawAgentOrchestratorCallbackDebugInfo && (
					<>
						<br />
						<br />
						<b>Detalhes da execução do Agente de IA:</b>
						<br />
						{_.isEmpty(extraData?.aiAgent?.rawAgentOrchestratorCallbackDebugInfo) ? (
							<>
								Os detalhes de execução encontram-se na última mensagem do agente nesta interação.
							</>
						) : (
							<JsonEditor
								mode="json"
								value={extraData.aiAgent.rawAgentOrchestratorCallbackDebugInfo}
								maxLines={10}
								readOnly
							/>
						)}
					</>
				)}
			</>
		)
	}

	const handleGetAIEquivalentVariationAdditionalInfo = () => {
		return (
			<>
				{extraData?.aiEquivalentVariation?.enabled && (
					<>
						<br />
						<br />
						<b>Conteúdo original da Variação com IA:</b> {extraData?.aiEquivalentVariation?.originalContent}
						<br />
						<b>Status da Variação com IA:</b> {extraData?.aiEquivalentVariation?.status}
						{extraData?.aiEquivalentVariation?.error && (
							<>
								<br />
								<b>Erro da Variação com IA:</b> {extraData?.aiEquivalentVariation?.error?.message}
							</>
						)}
					</>
				)}
			</>
		)
	}

	const handleShowAdditionalInfo = () => {
		handleClose()

		InfoDialog.open({
			title: "Informações adicionais",
			children: (
				<Typography
					variant="body1"
				>
					<b>ID da Mensagem:</b> {messageId}
					<br />
					<b>ID do Chat:</b> {inboxChannelChatId}
					<br />
					<br />
					<b>Tipo da Mensagem:</b> {type}
					<br />
					<b>Status da Mensagem:</b> {status}
					<br />
					<b>Conteúdo da Mensagem:</b> {content}
					{handleGetAIEquivalentVariationAdditionalInfo()}
					{HardCoded.checkFeatureFlag("canViewAgentDebugInfo") && handleGetAgentDebugInfo()}
					<br />
					<br />
					<b>Data de criação da Mensagem (Whatsapp):</b> {new Date(createdAt).toLocaleString()}
					<br />
					<b>Data de criação da Mensagem (Inbox):</b> {extraData?.inboxCreatedAt ? (
						new Date(extraData?.inboxCreatedAt).toLocaleString()
					) : "Data não encontrada"}
					{messageError &&
						<>
							<br />
							<b> Erro:</b>{messageError as string}
						</>}
				</Typography>
			),
			openDialog: true
		})
	}

	const isGroupChat = inboxChannelChat?.type === "group"
	const messageOptionsPopoverOpened = Boolean(messageOptionsRef)
	const showExternalPlatformIcon = sentByExternalPlatform && sentByCustomer
	const canShowMessageOptions = showMessageOptionsButton || canShowForwardOption
	const isOptionsButtonRequested = optionsIconVisible || isSmallScreen || messageOptionsPopoverOpened
	const showOptionsButton = (canShowMessageOptions && isOptionsButtonRequested) || isAdmin

	if (isGroupChat && replyMessage) {
		replyMessage.senderName = message?.replyMessage?.senderName || ""
	}

	return useCustomMemo(() => (
		<>
			{canShowForwardOption && (
				<ForwardMessageDialog
					isOpen={isForwardDialogOpen}
					message={message}
					onClose={handleCloseForwardDialog}
				/>
			)}

			<Grid
				container
				direction="column"
				className={classes.messageContainer}
				justifyContent={sentByCustomer ? "flex-end" : "flex-start"}
				alignItems={sentByCustomer ? "flex-end" : "flex-start"}
				style={{ paddingBottom: reactions ? 22 : "auto" }}
			>
				<MessageContentBox
					id={messageId}
					onMouseEnter={() => handleShowOptionsIcon()}
					onMouseLeave={() => handleHideOptionsIcon()}
					onFocus={() => handleShowOptionsIcon()}
					onBlur={handleMessageContentBlur}
					tabIndex={0}
					uploadingMedia={uploadingMedia}
					mediaUploadProgress={uploadProgressInPercentage}
					style={{
						...ColorUtil.getBackgroundAndContrastColor(getInboxMessageColor(sentByCustomer, status)),
						...messageContentBoxStyle
					}}
				>
					{catalogueOrderMessage && <CatalogueMessage {...catalogueOrderMessage} />}
					{extendedMessageHasExtendedUrl && <ExtendedMessage {...extendedMessage} fileName={message.fileName} />}
					<Grid
						container
						alignItems="center"
						justifyContent="flex-end"
						className={classes.messageInfoContainer}
					>
						{showExternalPlatformIcon && (
							<Grid className={classes.infoIconContainer}>
								<Tooltip
									title="Essa mensagem foi enviada pelo Whatsapp (Celular ou Web)."
								>
									<ExternalPlatformIcon
										fontSize="small"
										className={classes.infoIcon}
									/>
								</Tooltip>
							</Grid>
						)}

						{extraData?.flowResponse && (
							<Grid className={classes.infoIconContainer}>
								<Tooltip title="Resposta de formulário WhatsApp Flow">
									<FlowResponseIcon
										fontSize="small"
										className={classes.infoIcon}
									/>
								</Tooltip>
							</Grid>
						)}

						<MessageFeature
							message={message}
							isAdmin={isAdmin}
							channelType={chatGlobalStateStore.chat.current?.channelType}
						/>

						{showOptionsButton && (
							<>
								<Popover
									open={messageOptionsPopoverOpened}
									anchorEl={messageOptionsRef}
									onClose={handleCloseMessageOptionsPopover}
									anchorOrigin={{ vertical: "top", horizontal: "left" }}
									transformOrigin={{ vertical: "top", horizontal: "left" }}
								>
									<List>
										{isAbleReplyMessageOption && currentChatAttendanceStatus === "active" && <ListItem
											button
											onClick={() => handleReplyMessage()}
										>
											<ListItemText primary="Responder" />
										</ListItem>}

										{canShowForwardOption && (
											<ListItem
												button
												onClick={handleOpenForwardDialog}
											>
												<ListItemText primary="Encaminhar" />
											</ListItem>
										)}

										{canShowDeleteOption && (
											<ChatGroupPermissionGate action="delete-message">
												<ListItem
													button
													onClick={() => handleDeleteMessage()}
												>
													<ListItemText primary="Apagar para todos" />
												</ListItem>
											</ChatGroupPermissionGate>
										)}

										{showMessageOptionsButton && (
											<ListItem
												button
												onClick={handleShowAdditionalInfo}
											>
												<ListItemText primary="Informações adicionais" />
											</ListItem>
										)}
									</List>
								</Popover>

								<Fab
									onClick={(event) => handleOpenMessageOptionsPopover(event)}
									/**
									 * Setting up that way instead of using className since for some reason
									 * the className disappears in production.
									 */
									style={{
										minHeight: 30,
										height: 30,
										width: 30,
										boxShadow: "none",
										...ColorUtil.getBackgroundAndContrastColor("transparent")
									}}
								>
									<MessageOptionsIcon />
								</Fab>
							</>
						)}
					</Grid>

					{(replyMessage && !deletedAt) && (
						<>
							<ReplyMessage
								id={replyMessage.id}
								senderName={replyMessage.senderName}
								sentByCustomer={replyMessage.sentByCustomer}
								content={replyMessage.content}
								type={replyMessage.type}
								caption={replyMessage.caption}
								extraData={replyMessage.extraData}
							/>

							<Divider orientation="horizontal" size={1} />
						</>
					)}
					{HardCoded.checkFeatureFlag("isAbleToSeeStatusMessageResponse") && statusMessage && <>
						<Grid item>
							<Typography variant="body2"> Resposta de status</Typography>
						</Grid>
						<Grid item>
							<MediaMessageContent
								content={statusMessage.thumbnail}
								caption={message.caption}
								type={"picture"}
								fancyBoxSlug={message.fileName}
								fileName={message.fileName}
							/>
						</Grid>
						<Divider orientation="horizontal" size={1} />
					</>}

					{(isGroupChat && !sentByCustomer) && (
						<>
							<Typography
								variant="caption"
								className={classes.messageSenderName}
							>
								{message.senderName}
							</Typography>

							<Divider orientation="horizontal" size={0.5} />
						</>
					)}

					{childContentHeader && (
						<>
							{childContentHeader}

							<Divider orientation="horizontal" size={0.5} />

							<MuiDivider />

							<Divider orientation="horizontal" size={0.5} />
						</>
					)}

					{deletedAt ? (
						<MessageDeletedContent
							sentByCustomer={sentByCustomer}
						/>
					) : (
						childContent
					)}

					<Divider orientation="horizontal" size={1} />

					<InboxMessageFooter
						status={status}
						sentByExternalPlatform={sentByExternalPlatform}
						senderName={senderName}
						sentByCustomer={sentByCustomer}
						messageExtraData={message?.extraData}
						feature={feature}
						createdAt={createdAt}
						deletedAt={deletedAt}
						messageId={messageId}
						error={messageError}
						channelType={inboxChannelChat.channelType}
					/>

					{childContentFooter && (
						<>
							<Divider orientation="horizontal" size={0.5} />

							<MuiDivider />

							<Divider orientation="horizontal" size={0.5} />

							{childContentFooter}
						</>
					)}

					{reactions && <Reactions reactions={reactions} maxReactionsAllowed={2} />}
				</MessageContentBox>

				{childContainerFooter && (
					<>
						<Divider orientation="horizontal" size={0.5} />

						{childContainerFooter}
					</>
				)}
			</Grid>
		</>

	), [
		createdAt,
		deletedAt,
		content,
		status,
		editedMessage,
		messageError,
		uploadingMedia,
		optionsIconVisible,
		messageOptionsRef,
		currentChatAttendanceStatus,
		reactionsString,
		uploadProgressInPercentage,
		message.replyMessage?.id,
		message.replyMessage?.content,
		message.replyMessage?.caption,
		message.replyMessage?.type,
		message.replyMessage?.sentByCustomer,
		isForwardDialogOpen,
		canShowForwardOption
	])
}

export default BaseMessage
